import React from 'react';
import { CheckCircle2, ArrowRight, Crown } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import UserPlanBadge from '../UserPlanBadge';
import { useLanguage } from '../../contexts/LanguageContext';
import './plansTheme.css';

export default function PlanCards({ plans, currentPlan, loading, onDowngrade, onUpgrade }) {
  const { language } = useLanguage();
  const es = language === 'es';
  const entries = Object.entries(plans);
  if (!entries.length) return null;
  return (
    <section aria-label={es ? 'Planes disponibles' : 'Available plans'} className="space-y-8">
      <header className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-violet-700">
          {es ? 'Planes de Lexa' : 'Lexa plans'}
        </p>
        <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
          {es ? 'Planes que crecen con tu equipo' : 'Plans that scale with your team'}
        </h2>
        <p className="text-sm text-slate-500">
          {es ? 'Compara los límites y elige el plan adecuado para tu workspace.' : 'Compare limits and choose the right plan for your workspace.'}
        </p>
        <span className="inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600">
          {es ? 'Facturación mensual' : 'Monthly billing'}
        </span>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {entries.map(([planId, plan]) => {
          const current = currentPlan.toUpperCase() === planId.toUpperCase();
          const free = planId === 'TRIAL' || planId === 'free';
          const enterprise = planId.toUpperCase() === 'ENTERPRISE';
          const highlighted = Boolean(plan.highlight_badge || plan.popular);
          const executions = plan.executions_monthly || plan.monthly_tests;
          const unlimited = es ? 'Sin límite' : 'Unlimited';
          return (
            <Card key={planId} className={`lexa-plan-card relative rounded-2xl border-2 bg-white p-6 shadow-none flex flex-col ${
              current || highlighted ? 'border-[#6d28d9]' : 'border-slate-200'
            } ${enterprise ? 'md:col-span-2 xl:col-span-4' : ''}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
                <UserPlanBadge plan={planId} displayName={plan.name} />
                {current ? (
                  <span className="rounded-full bg-[#6d28d9] px-3 py-1 text-xs font-medium text-white">
                    {es ? 'Plan actual' : 'Current Plan'}
                  </span>
                ) : plan.highlight_badge ? (
                  <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700">
                    {plan.highlight_badge}
                  </span>
                ) : null}
              </div>
              <div className={enterprise ? 'grid md:grid-cols-3 gap-6 items-start' : 'flex flex-col flex-1'}>
                <div className="text-center mb-6">
                  {enterprise && <Crown className="h-6 w-6 text-violet-700 mx-auto mb-3" aria-hidden="true" />}
                  <h3 className="text-xl font-semibold tracking-tight text-slate-950">{plan.name}</h3>
                  <div className="text-4xl font-semibold tracking-tight text-slate-950 mt-3">
                    ${plan.price_usd || plan.price}
                    <span className="text-sm font-normal text-slate-500">{es ? '/mes' : '/month'}</span>
                  </div>
                </div>
                <div className="space-y-3 mb-6 text-sm text-slate-600 flex-1">
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
                    <span><strong>{executions === -1 ? unlimited : executions}</strong> {es ? 'ejecuciones' : 'executions'}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
                    <span><strong>{plan.team_members_included === -1 ? unlimited : plan.team_members_included}</strong> {es ? 'miembros del equipo' : `team member${plan.team_members_included !== 1 ? 's' : ''}`}</span>
                  </p>
                  {plan.overage_enabled && (
                    <p>{es ? 'Uso adicional' : 'Overage'}: ${plan.overage_price_usd || 'Custom'}/{es ? 'ejecución' : 'execution'}</p>
                  )}
                  <ul className="space-y-3">
                    {(plan.features || []).map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {plan.notes && <p className="text-xs text-slate-500 italic">{plan.notes}</p>}
                </div>
                {!current && (
                  <Button
                    onClick={() => free ? onDowngrade(planId) : onUpgrade(planId.toUpperCase())}
                    disabled={loading || plan.contract_required}
                    className="w-full rounded-full py-3 whitespace-normal h-auto min-h-10 bg-[#6d28d9] hover:bg-[#5b21b6] text-white font-semibold"
                  >
                    {plan.contract_required ? (es ? 'Contactar ventas' : 'Contact Sales')
                      : free ? (es ? 'Cambiar a gratuito' : 'Downgrade')
                        : `${es ? 'Cambiar a' : 'Upgrade to'} ${plan.name}`}
                    <ArrowRight className="h-4 w-4 ml-2 shrink-0" aria-hidden="true" />
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
